/** Persist muted friends per account (device-local until a mute API exists). */

export const MUTED_FRIENDS_CHANGED_EVENT = "c8-muted-friends-changed";

function normalizeAccountId(id: unknown): number | undefined {
  if (id == null) return undefined;
  if (typeof id === "number") return Number.isFinite(id) && id > 0 ? id : undefined;
  if (typeof id === "string") {
    const n = parseInt(id, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }
  return undefined;
}

function storageKey(accountUserId: number): string {
  return `c8_muted_friend_ids:${accountUserId}`;
}

function parseIds(raw: string | null): number[] {
  if (raw == null || raw === "") return [];
  try {
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    const out: number[] = [];
    for (const x of v) {
      const n = typeof x === "number" ? x : parseInt(String(x), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
    return [...new Set(out)];
  } catch {
    return [];
  }
}

export function readMutedFriendIdsArray(accountUserId: number): number[] {
  if (typeof window === "undefined") return [];
  return parseIds(window.localStorage.getItem(storageKey(accountUserId)));
}

export function getMutedFriendIdsSet(accountUserId: unknown): Set<number> {
  const id = normalizeAccountId(accountUserId);
  if (id == null) return new Set();
  return new Set(readMutedFriendIdsArray(id));
}

function writeMutedFriendIdsArray(accountUserId: number, ids: number[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(storageKey(accountUserId), JSON.stringify(ids));
}

function dispatchMutedFriendsChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MUTED_FRIENDS_CHANGED_EVENT));
}

export function addMutedFriend(accountUserId: unknown, friendUserId: unknown): void {
  const aid = normalizeAccountId(accountUserId);
  const fid = normalizeAccountId(friendUserId);
  if (aid == null || fid == null) return;
  const ids = readMutedFriendIdsArray(aid);
  if (ids.includes(fid)) {
    dispatchMutedFriendsChanged();
    return;
  }
  ids.push(fid);
  writeMutedFriendIdsArray(aid, ids);
  dispatchMutedFriendsChanged();
}

export function removeMutedFriend(accountUserId: unknown, friendUserId: unknown): void {
  const aid = normalizeAccountId(accountUserId);
  const fid = normalizeAccountId(friendUserId);
  if (aid == null || fid == null) return;
  const next = readMutedFriendIdsArray(aid).filter((id) => id !== fid);
  writeMutedFriendIdsArray(aid, next);
  dispatchMutedFriendsChanged();
}

/** Max IDs appended to unread-count query (server also caps). */
const MAX_EXCLUDE_QUERY_IDS = 200;

export function excludeMutedSendersQueryParam(accountUserId: unknown): string {
  const id = normalizeAccountId(accountUserId);
  if (id == null) return "";
  const ids = readMutedFriendIdsArray(id).slice(0, MAX_EXCLUDE_QUERY_IDS);
  if (ids.length === 0) return "";
  return `exclude_sender_ids=${ids.map((id) => encodeURIComponent(String(id))).join(",")}`;
}

export function getNotificationSenderUserId(n: Record<string, unknown>): number | null {
  const sender = n.sender as { userId?: unknown } | null | undefined;
  if (sender && typeof sender.userId === "number" && Number.isFinite(sender.userId)) {
    return sender.userId;
  }
  const sid = n.sender_id;
  if (typeof sid === "number" && Number.isFinite(sid)) return sid;
  if (typeof sid === "string") {
    const p = parseInt(sid, 10);
    return Number.isFinite(p) ? p : null;
  }
  return null;
}

export function isNotificationFromMutedFriend(n: Record<string, unknown>, muted: Set<number>): boolean {
  const id = getNotificationSenderUserId(n);
  if (id == null) return false;
  return muted.has(id);
}

/** API rows may use numeric or string `userId`; muted set is always numeric from storage. */
export function isFriendUserIdMuted(muted: Set<number>, rawFriendUserId: unknown): boolean {
  if (typeof rawFriendUserId === "number" && Number.isFinite(rawFriendUserId)) {
    return muted.has(rawFriendUserId);
  }
  if (typeof rawFriendUserId === "string") {
    const n = parseInt(rawFriendUserId, 10);
    return Number.isFinite(n) ? muted.has(n) : false;
  }
  return false;
}
